import React, { useMemo, useState } from "react";
import { Box, ButtonBase, IconButton, Popover } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

/*
 * ThemedDatePicker — drop-in replacement for <input type="date">.
 * Value in/out is the same "YYYY-MM-DD" string, and onChange receives
 * { target: { name, value } }, so existing handleChange functions work as-is.
 *
 *   <ThemedDatePicker name="startDate" value={form.startDate}
 *     onChange={handleChange} error={!!errors.startDate} min="2026-01-01" />
 */

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";
const ERROR = "#C42B24";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Parse "YYYY-MM-DD" as a local date (new Date("YYYY-MM-DD") would be UTC and can shift a day).
const fromISO = (s) => {
  if (!s || typeof s !== "string") return null;
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const display = (s) => {
  const d = fromISO(s);
  return d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : "";
};

const ThemedDatePicker = ({
  name,
  id,
  value,
  onChange,
  error = false,
  min,
  max,
  placeholder = "dd/mm/yyyy",
  disabled = false,
  height = 42,
  fullWidth = true,
  sx,
}) => {
  const [anchor, setAnchor] = useState(null);
  const selected = fromISO(value);
  const today = useMemo(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  }, []);
  const [view, setView] = useState(() => {
    const base = selected || today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const minD = fromISO(min);
  const maxD = fromISO(max);

  const open = (e) => {
    if (disabled) return;
    const base = fromISO(value) || today;
    setView(new Date(base.getFullYear(), base.getMonth(), 1));
    setAnchor(e.currentTarget);
  };
  const close = () => setAnchor(null);
  const emit = (v) => onChange && onChange({ target: { name, value: v, id } });
  const pick = (d) => {
    emit(toISO(d));
    close();
  };

  const isDisabled = (d) => (minD && d < minD) || (maxD && d > maxD);
  const same = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  // 6 rows × 7 days, starting on the Sunday before the 1st.
  const cells = useMemo(() => {
    const start = new Date(view.getFullYear(), view.getMonth(), 1 - view.getDay());
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [view]);

  const shift = (months) => setView((v) => new Date(v.getFullYear(), v.getMonth() + months, 1));

  return (
    <>
      <ButtonBase
        id={id}
        name={name}
        onClick={open}
        disabled={disabled}
        disableRipple
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          width: fullWidth ? "100%" : "auto",
          minWidth: 150,
          height,
          px: 1.5,
          boxSizing: "border-box",
          background: disabled ? "#F4F7FA" : "#FFFFFF",
          border: `1px solid ${error ? ERROR : anchor ? PRIMARY : FIELD_EDGE}`,
          borderRadius: "10px",
          fontFamily: MONO,
          fontSize: 13.5,
          color: value ? INK : "#8A97A8",
          textAlign: "left",
          transition: "border-color .15s",
          "&:hover": { borderColor: error ? ERROR : anchor ? PRIMARY : "#B4C6DA" },
          ...sx,
        }}
      >
        <span>{display(value) || placeholder}</span>
        <CalendarTodayOutlinedIcon sx={{ fontSize: 17, color: anchor ? PRIMARY : MUTED }} />
      </ButtonBase>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: 296,
              p: 1.5,
              borderRadius: "14px",
              border: `1px solid ${LINE}`,
              boxShadow: "0 16px 36px -12px rgba(15,27,42,0.28)",
              fontFamily: FONT,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ fontFamily: FONT, fontSize: 14.5, fontWeight: 800, letterSpacing: "-0.01em", color: INK, pl: 0.5 }}>
            {MONTHS[view.getMonth()]} {view.getFullYear()}
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton size="small" onClick={() => shift(-1)} sx={{ width: 30, height: 30, borderRadius: "8px", color: INK_2, "&:hover": { background: "#F4F7FA" } }}>
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton size="small" onClick={() => shift(1)} sx={{ width: 30, height: 30, borderRadius: "8px", color: INK_2, "&:hover": { background: "#F4F7FA" } }}>
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", mb: 0.5 }}>
          {WEEKDAYS.map((w) => (
            <Box key={w} sx={{ textAlign: "center", py: 0.5, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: MUTED }}>
              {w}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
          {cells.map((d) => {
            const inMonth = d.getMonth() === view.getMonth();
            const isSel = same(d, selected);
            const isToday = same(d, today);
            const off = isDisabled(d);
            return (
              <ButtonBase
                key={d.toISOString()}
                disabled={off}
                onClick={() => pick(d)}
                sx={{
                  height: 36,
                  borderRadius: "9px",
                  fontFamily: MONO,
                  fontSize: 13,
                  fontWeight: isSel || isToday ? 700 : 500,
                  color: isSel ? "#FFFFFF" : off ? "#C3CDD9" : inMonth ? INK : "#A9B4C2",
                  background: isSel ? PRIMARY : "transparent",
                  boxShadow: !isSel && isToday ? `inset 0 0 0 1.5px ${PRIMARY}` : "none",
                  textDecoration: off ? "line-through" : "none",
                  "&:hover": { background: isSel ? "#084767" : "#EAF2FB" },
                }}
              >
                {d.getDate()}
              </ButtonBase>
            );
          })}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1.25, pt: 1.25, borderTop: "1px solid #EDF1F5" }}>
          <ButtonBase
            onClick={() => { emit(""); close(); }}
            sx={{ height: 30, px: 1.25, borderRadius: "8px", fontFamily: FONT, fontSize: 13, fontWeight: 600, color: MUTED, "&:hover": { background: "#F4F7FA", color: INK } }}
          >
            Clear
          </ButtonBase>
          <ButtonBase
            onClick={() => !isDisabled(today) && pick(today)}
            disabled={!!isDisabled(today)}
            sx={{ height: 30, px: 1.25, borderRadius: "8px", fontFamily: FONT, fontSize: 13, fontWeight: 700, color: PRIMARY, "&:hover": { background: "#EAF2FB" }, "&.Mui-disabled": { color: "#C3CDD9" } }}
          >
            Today
          </ButtonBase>
        </Box>
      </Popover>
    </>
  );
};

export default ThemedDatePicker;
